import { calculateTotalFees, normalizeCadenceDiscountPct } from '../../utils/calculateTotalFees';
import { formatCadenceOptionLabel } from '../../utils/cadenceAdjustments';
import { getCurrencyMeta } from '../../utils/currency';
import { resolveCycleBasePricing } from '../../utils/subscriptionPricing';

function safeNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

export function formatMinorAmount(minor, currency = 'GBP') {
  const { symbol, decimals } = getCurrencyMeta(currency);
  const major = safeNumber(minor, 0) / Math.pow(10, decimals);
  return `${symbol}${major.toFixed(decimals)}`;
}

function defaultCadenceOptions(setup = {}, cadenceMode = 'fixed') {
  if (cadenceMode === 'member_choice') {
    const memberChoice = setup?.memberChoice || {};
    return [
      {
        key: 'monthly',
        label: 'Monthly',
        unit: 'month',
        count: 1,
        discountPct: normalizeCadenceDiscountPct(memberChoice?.monthly?.discountPct),
      },
      {
        key: 'yearly',
        label: 'Yearly',
        unit: 'year',
        count: 1,
        discountPct: normalizeCadenceDiscountPct(memberChoice?.yearly?.discountPct),
      },
    ];
  }

  const unit = setup?.fixedInterval?.unit === 'month' ? 'month' : 'year';
  return [{
    key: unit === 'month' ? 'monthly' : 'yearly',
    label: unit === 'month' ? 'Monthly' : 'Yearly',
    unit,
    count: 1,
    discountPct: 0,
  }];
}

function normalizeOptions({ setup = {}, cadenceMode = 'fixed', cadenceOptions = [] } = {}) {
  const options = Array.isArray(cadenceOptions) && cadenceOptions.length > 0
    ? cadenceOptions
    : defaultCadenceOptions(setup, cadenceMode);
  return options
    .map((option) => ({
      key: option?.key || (option?.unit === 'month' ? 'monthly' : 'yearly'),
      label: option?.label || (option?.unit === 'month' ? 'Monthly' : 'Yearly'),
      unit: option?.unit === 'month' ? 'month' : 'year',
      count: Math.max(1, safeNumber(option?.count, 1)),
      discountPct: normalizeCadenceDiscountPct(option?.discountPct),
    }))
    .filter((option) => option.unit === 'month' || option.unit === 'year');
}

function optionAmount({
  setup = {},
  option,
  membershipPlanType = 'individual',
  selectedFamilyTier = null,
  memberCount = 1,
  appliedTags = [],
  preferredTag = '',
  externalPayment = false,
}) {
  const isFamilyTier = membershipPlanType === 'family_tier';
  const pricingBaseRate = isFamilyTier ? safeNumber(selectedFamilyTier?.baseRate, 0) : safeNumber(setup?.baseRate, 0);
  const pricingBaseRateYearly = isFamilyTier ? selectedFamilyTier?.baseRateYearly : setup?.baseRateYearly;
  const basePricing = resolveCycleBasePricing({
    baseRate: pricingBaseRate,
    baseRateYearly: pricingBaseRateYearly,
    tagOverrides: isFamilyTier ? {} : setup?.tagOverrides || {},
    tagOverridesYearly: isFamilyTier ? {} : setup?.tagOverridesYearly || {},
    appliedTags: isFamilyTier ? [] : appliedTags,
    preferredTag: isFamilyTier ? '' : preferredTag,
    cadenceUnit: option.unit,
    cadenceCount: option.count,
  });
  const platformFeeMultiplier = isFamilyTier ? Math.max(1, safeNumber(memberCount, 1)) : 1;
  const platformFeeMinor = Number.isFinite(Number(setup?.platformFeeMinor))
    ? Number(setup.platformFeeMinor) * platformFeeMultiplier
    : setup?.platformFeeMinor;
  const fees = calculateTotalFees({
    baseAmount: Math.round(safeNumber(basePricing.baseForCycle, 0)),
    cadence: {
      unit: option.unit,
      count: option.count,
      discountPct: option.discountPct || 0,
    },
    platformFeeHandling: externalPayment ? 'deduct' : setup?.platformFeeHandling || 'deduct',
    stripeFeeHandling: externalPayment ? 'deduct' : setup?.stripeFeeHandling || 'deduct',
    currency: setup?.currency || 'GBP',
    platformFeeMinor,
  });
  return {
    ...option,
    ...fees,
  };
}

function formatOptionLine(option, currency) {
  if (option.unit === 'month') {
    return `${formatCadenceOptionLabel(option.label, option.discountPct)}: ${formatMinorAmount(option.finalAmount, currency)}/month`;
  }
  const monthlyEquivalent = Math.round(safeNumber(option.finalAmount, 0) / 12);
  return `${formatCadenceOptionLabel(option.label, option.discountPct)}: ${formatMinorAmount(option.finalAmount, currency)}/year (${formatMinorAmount(monthlyEquivalent, currency)}/month equivalent)`;
}

function formatSummary(option, currency) {
  if (!option) return '';
  if (option.unit === 'month') {
    return `${formatCadenceOptionLabel(option.label, option.discountPct)}, ${formatMinorAmount(option.finalAmount, currency)}/month`;
  }
  const monthlyEquivalent = Math.round(safeNumber(option.finalAmount, 0) / 12);
  return `${formatCadenceOptionLabel(option.label, option.discountPct)}, ${formatMinorAmount(option.finalAmount, currency)}/year (${formatMinorAmount(monthlyEquivalent, currency)}/month equivalent)`;
}

export function buildJoinBillingPreview({
  stripeSetup = {},
  cadenceMode = 'fixed',
  cadenceOptions = [],
  selectedCadence = null,
  showSelectedMemberChoice = false,
  membershipPlanType = 'individual',
  selectedFamilyTier = null,
  memberCount = 1,
  appliedTags = [],
  preferredTag = '',
  externalPayment = false,
} = {}) {
  const setup = stripeSetup || {};
  const currency = setup?.currency || 'GBP';
  const options = normalizeOptions({ setup, cadenceMode, cadenceOptions })
    .map((option) => optionAmount({
      setup,
      option,
      membershipPlanType,
      selectedFamilyTier,
      memberCount,
      appliedTags,
      preferredTag,
      externalPayment,
    }));

  if (options.length === 0) return null;

  if (cadenceMode === 'member_choice') {
    const selected = showSelectedMemberChoice && selectedCadence
      ? options.find((option) => (
        option.key === selectedCadence.key
        || (option.unit === selectedCadence.unit && option.count === selectedCadence.count)
      ))
      : null;
    return {
      mode: 'member_choice',
      summaryLabel: selected ? formatSummary(selected, currency) : 'Choose monthly or yearly at payment',
      description: externalPayment
        ? 'This club offers monthly and yearly membership fees paid outside Member Nest.'
        : 'This club offers monthly and yearly billing.',
      lines: options.map((option) => formatOptionLine(option, currency)),
      note: selected
        ? externalPayment
          ? 'This is the club-stated fee; payment is arranged with the club.'
          : 'You can review the selected billing frequency before payment.'
        : externalPayment
          ? 'Choose your membership frequency on the club payment step.'
          : 'Choose your billing frequency on the payment step.',
    };
  }

  const fixed = options[0];
  const isMonthly = fixed.unit === 'month';
  return {
    mode: isMonthly ? 'fixed_monthly' : 'fixed_yearly',
    summaryLabel: formatSummary(fixed, currency),
    description: externalPayment
      ? `This club states a ${isMonthly ? 'monthly' : 'yearly'} membership fee paid outside Member Nest.`
      : isMonthly
        ? 'This club charges this membership monthly.'
        : 'This club charges this membership yearly.',
    lines: [formatOptionLine(fixed, currency)],
    note: externalPayment
      ? 'Payment is arranged directly with the club.'
      : isMonthly ? '' : 'Monthly equivalent is shown for comparison only.',
  };
}
